/**
 * BookingCancelPage
 *
 * Public (no auth) page for cancelling a booking via cancellation token.
 * Reads bookingId and token from URL search params.
 */

import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

type Step = 'confirm' | 'cancelling' | 'cancelled' | 'error';

export function BookingCancelPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId') || '';
  const token = searchParams.get('token') || '';

  const [step, setStep] = useState<Step>('confirm');
  const [errorMessage, setErrorMessage] = useState('');

  const handleCancel = async () => {
    if (!slug || !bookingId || !token) {
      setErrorMessage('Invalid cancellation link');
      setStep('error');
      return;
    }

    setStep('cancelling');
    try {
      await api.cancelPublicBooking(slug, bookingId, token);
      setStep('cancelled');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to cancel booking');
      setStep('error');
    }
  };

  if (!bookingId || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <XCircle className="h-12 w-12 text-destructive mx-auto" />
          <p className="text-lg font-medium">Invalid cancellation link</p>
          <p className="text-sm text-muted-foreground">The link may be expired or malformed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-sm w-full mx-4 text-center space-y-4">
        {step === 'confirm' && (
          <>
            <h1 className="text-xl font-bold">Cancel Booking?</h1>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to cancel this booking? This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => window.history.back()}>
                Go Back
              </Button>
              <Button variant="destructive" onClick={handleCancel}>
                Yes, Cancel Booking
              </Button>
            </div>
          </>
        )}

        {step === 'cancelling' && (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
        )}

        {step === 'cancelled' && (
          <>
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-xl font-bold">Booking Cancelled</h1>
            <p className="text-sm text-muted-foreground">
              Your booking has been successfully cancelled.
            </p>
          </>
        )}

        {step === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <p className="text-lg font-medium text-destructive">{errorMessage}</p>
            <Button variant="outline" onClick={handleCancel}>
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
